"use client"

import { addToCart } from "@lib/data/cart"
import { getProductPrice } from "@lib/util/get-product-price"
import type { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import PlaceholderImage from "@modules/common/icons/placeholder-image"
import { useCartDrawer } from "@modules/cart-drawer/context/cart-drawer-context"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { useMemo, useRef, useState } from "react"
import styles from "./collection-product-card.module.css"

const variantAvailable = (variant: HttpTypes.StoreProductVariant | undefined) => {
  if (!variant) return false
  if (!variant.manage_inventory || variant.allow_backorder) return true
  return (variant.inventory_quantity ?? 0) > 0
}

export default function CollectionProductCard({
  product,
  countryCode,
  hasBundles = false,
  surface = "default",
}: {
  product: HttpTypes.StoreProduct
  countryCode: string
  hasBundles?: boolean
  surface?: "default" | "homepage"
}) {
  const router = useRouter()
  const { replaceSnapshot, openCart } = useCartDrawer()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mobileImage, setMobileImage] = useState<0 | 1>(0)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const { cheapestPrice } = useMemo(() => getProductPrice({ product }), [product])

  const images = (product.images ?? []).map((image) => image.url).filter(Boolean) as string[]
  const primaryImage = product.thumbnail || images[0] || null
  const secondaryImage = images.find((url) => url !== primaryImage) || null
  const variants = product.variants ?? []
  const directVariant = variants.length === 1 ? variants[0] : undefined
  const canQuickAdd = !hasBundles && Boolean(directVariant?.id && variantAvailable(directVariant))
  const requiresSelection = hasBundles || variants.length !== 1
  const soldOut = variants.length > 0 && !variants.some(variantAvailable)
  const isSale = Boolean(
    cheapestPrice &&
      cheapestPrice.original_price_number > cheapestPrice.calculated_price_number
  )
  const href = `/products/${product.handle}`

  const handleImageTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0]
    if (!touch || !secondaryImage) return
    touchStartRef.current = { x: touch.clientX, y: touch.clientY }
  }

  const handleImageTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const start = touchStartRef.current
    const touch = event.changedTouches[0]
    touchStartRef.current = null
    if (!start || !touch || !secondaryImage) return

    const deltaX = touch.clientX - start.x
    const deltaY = touch.clientY - start.y
    if (Math.abs(deltaX) < 32 || Math.abs(deltaX) <= Math.abs(deltaY)) return

    setMobileImage(deltaX < 0 ? 1 : 0)
  }

  const handleQuickAdd = async () => {
    if (!directVariant?.id || !canQuickAdd || pending) return
    setPending(true)
    setError(null)
    try {
      const snapshot = await addToCart({
        variantId: directVariant.id,
        quantity: 1,
        countryCode,
      })
      replaceSnapshot(snapshot)
      openCart()
      router.refresh()
    } catch {
      setError("Ürün sepete eklenemedi.")
    } finally {
      setPending(false)
    }
  }

  return (
    <article
      className={styles.card}
      data-testid="collection-product-card"
      data-surface={surface}
    >
      <LocalizedClientLink href={href} className={styles.productLink}>
        <div
          className={styles.imageWrap}
          data-testid="collection-card-image"
          data-mobile-image={mobileImage}
          onTouchStart={handleImageTouchStart}
          onTouchEnd={handleImageTouchEnd}
        >
          <div
            className={styles.imageTrack}
            data-testid="collection-card-image-track"
            data-has-secondary={secondaryImage ? "true" : "false"}
          >
            <div className={styles.imageSlide} data-testid="collection-card-image-slide">
              {primaryImage ? (
                <Image
                  src={primaryImage}
                  alt={product.title}
                  fill
                  className={styles.image}
                  sizes="(min-width: 1280px) 300px, (min-width: 960px) 30vw, 50vw"
                  draggable={false}
                />
              ) : (
                <div className={styles.placeholder}><PlaceholderImage size={28} /></div>
              )}
            </div>
            {secondaryImage ? (
              <div className={styles.imageSlide} data-testid="collection-card-image-slide">
                <Image
                  src={secondaryImage}
                  alt=""
                  fill
                  className={styles.image}
                  data-testid="collection-card-secondary-image"
                  data-mobile-active={mobileImage === 1 ? "true" : "false"}
                  sizes="(min-width: 1280px) 300px, (min-width: 960px) 30vw, 50vw"
                  draggable={false}
                  aria-hidden="true"
                />
              </div>
            ) : null}
          </div>
          {secondaryImage ? (
            <span className={styles.imageDots} data-testid="collection-card-image-dots" aria-hidden="true">
              <span data-active={mobileImage === 0 ? "true" : "false"} />
              <span data-active={mobileImage === 1 ? "true" : "false"} />
            </span>
          ) : null}
          {isSale && cheapestPrice ? (
            <span className={styles.discountBadge}>
              %{Math.max(1, Math.round(Number(cheapestPrice.percentage_diff)))} İndirim
            </span>
          ) : null}
        </div>

        <div className={styles.content}>
          <h2 className={styles.title}>{product.title}</h2>
          {cheapestPrice ? (
            <div className={styles.priceRow}>
              <span className={styles.price} data-testid="collection-card-price">{cheapestPrice.calculated_price}</span>
              {isSale ? <span className={styles.comparePrice} data-testid="collection-card-compare-price">{cheapestPrice.original_price}</span> : null}
            </div>
          ) : null}
        </div>
      </LocalizedClientLink>

      <div className={styles.ctaArea}>
        {requiresSelection ? (
          <LocalizedClientLink href={href} className={styles.ctaButton} data-testid="collection-card-options">
            Seçenekleri Seç
          </LocalizedClientLink>
        ) : (
          <button
            type="button"
            className={styles.ctaButton}
            onClick={handleQuickAdd}
            disabled={!canQuickAdd || pending}
            data-testid="collection-card-add"
          >
            {pending ? "Ekleniyor…" : soldOut ? "Tükendi" : "Sepete Ekle"}
          </button>
        )}
        {error ? <p className={styles.error} role="status">{error}</p> : null}
      </div>
    </article>
  )
}
